import { ESdChangeReason } from "@kuika/kuika-cl-model";
import { connect } from "react-redux";
import codeGenSseService from "../../../../../services/codegen-sse-service";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../../shared/pure-sd";
import { getSdInstance } from "../../../../../shared/utility/designer-context";
import { runDesignerPreview } from "../../../../../shared/utility/designer-project-actions";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { setNextCallbackFunction } from "../../../../../store/unsaved-changes-modal/actions";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import { PublishButton } from "./publish-popover";
import { getRunStatusLabel, LoadingSource, PreviewStatus } from "./run-status";
import { WorkflowVersionDetailsModal } from "./workflow-version-details-modal";

export interface IRunButtonState {
  popupVisible: boolean;
  previewStatus: PreviewStatus;
  loadingSource: LoadingSource;
}

export interface IRunButtonProps {
  setNextCallbackFunction: (cb?: () => void) => void;
}

interface IRunProjectDTO {
  url: string;
  urlBackend?: string;
}

class RunButton extends PureSdComponent<IRunButtonProps & IWithSDProps & IWithContextProp, IRunButtonState> {
  lastPreviewResult: IRunProjectDTO | undefined = undefined;

  constructor(props: IRunButtonProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      popupVisible: false,
      previewStatus: "idle",
      loadingSource: null
    };
  }

  componentDidMount() {
    codeGenSseService.setCodeGeneratedCallback((message) => {
      let messageObject: { status?: string; publishType?: string };
      try {
        messageObject = JSON.parse(message);
      } catch {
        return;
      }
      const newStatus =
        messageObject.status === "error" ? "error" : messageObject.status === "warning" ? "warning" : "success";
      this.setState({ previewStatus: newStatus });

      setTimeout(() => {
        this.setState({ previewStatus: "idle", loadingSource: null });
        codeGenSseService.removeAllListeners();

        if (messageObject.publishType !== "DownloadExecutables") codeGenSseService.stopConnection();
      }, 3000);
    });

    codeGenSseService.setErrorOccuredCallback((message) => {
      KMainFunctions.displayErrorNotification(message);
      const status = "error";
      this.setState({ previewStatus: status });

      setTimeout(() => {
        this.setState({ previewStatus: "idle", loadingSource: null });
        codeGenSseService.removeAllListeners();
        codeGenSseService.stopConnection();
      }, 3000);
    });

    codeGenSseService.setGenerationCancelledCallback(() => {
      this.setState({ previewStatus: "idle", loadingSource: null });
    });
  }

  componentWillUnmount() {
    codeGenSseService.setCodeGeneratedCallback(null);
    codeGenSseService.setErrorOccuredCallback(null);
    codeGenSseService.setGenerationCancelledCallback(null);
    codeGenSseService.removeAllListeners();
    codeGenSseService.stopConnection();
  }

  handlePreviewButtonClick = async () => {
    // popupVisible must not survive into the run: the split (and its popover) unmounts while
    // loading, and a stale true would reopen the popover by itself when the split remounts.
    this.setState({ previewStatus: "loading", loadingSource: "preview", popupVisible: false });

    const result = await runDesignerPreview(getSdInstance());
    if ("error" in result) {
      KMainFunctions.displayErrorNotification(result.error);
      this.setState({ previewStatus: "idle" });
      return;
    }
  };

  handleCancelClick = () => {
    this.setState({ previewStatus: "idle", loadingSource: null });
    this.props.sd.cancelCurrentGeneration();
  };

  setPreviewStatus = (
    status: "idle" | "loading" | "success" | "error" | "warning",
    source?: "preview" | "export" | "source-code"
  ) => {
    if (status === "loading") {
      // Entering a run unmounts the split + its popover; a stale open flag would reopen the
      // popover by itself when the split comes back (same guard as handlePreviewButtonClick).
      this.setState({ previewStatus: status, loadingSource: source || null, popupVisible: false });
      return;
    }
    this.setState({ previewStatus: status, loadingSource: source || null });
  };

  setVisible = (visible: boolean) => {
    this.setState({ popupVisible: visible });
  };

  /**
   * The glyph for every state but `loading`: while a run is in flight the button draws its ring
   * spinner and the ✕ instead, and this icon is not rendered.
   */
  getButtonIcon = (status: string): "player-play-circle" | "circle-check-2" | "circle-x-2" | "circle-warning" => {
    switch (status) {
      case "success":
        return "circle-check-2";
      case "error":
        return "circle-x-2";
      case "warning":
        return "circle-warning";
      default:
        // Circled play (Figma header-toolbar spec) — keeps the resting icon in the same
        // circular family as the spinner/check/error states it swaps with.
        return "player-play-circle";
    }
  };

  render() {
    const { previewStatus, loadingSource } = this.state;
    const isLoading = previewStatus === "loading";
    const buttonIcon = this.getButtonIcon(previewStatus);
    const statusLabel: string | null = getRunStatusLabel(previewStatus, loadingSource);
    return (
      <>
        <KuikaButton
          type="primary"
          size="sm"
          active={isLoading}
          className={`kuika-run__preview kuika-run__preview--${previewStatus}`}
          aria-label={isLoading ? "Cancel preview" : "Preview"}
          disabled={this.props.sd.configurations.length === 0}
          onClick={(e) => {
            if (
              document.querySelector(".kuika-popover")?.contains(e.target as Node) ||
              document.querySelector(".kuika-selectbox__dropdown")?.contains(e.target as Node)
            ) {
              return;
            }
            if (isLoading) {
              this.handleCancelClick();
            } else {
              this.handlePreviewButtonClick();
            }
          }}
          data-onboarding-id="header_toolbar-right_preview-button"
          // The "…" publish split hides while ANY generation is processing (preview, export,
          // download source, internal kuika publish — every flow drives previewStatus to
          // "loading"): starting a second flow mid-run isn't allowed anyway, and the collapsed
          // single square makes the running ✕/cancel state unambiguous.
          withIconButton={
            isLoading ? undefined : (
              <PublishButton
                showPopup={this.state.popupVisible}
                setShowPopup={this.setVisible}
                handlePreviewButtonClick={this.handlePreviewButtonClick}
                previewStatus={previewStatus}
                setPreviewStatus={this.setPreviewStatus}
                loadingSource={loadingSource}
                data-onboarding-id="header_toolbar-right_publish-button"
              >
                <KuikaButton
                  data-testid="openPublishModalThreedotButton"
                  data-onboarding-id="header_toolbar-right_publish-options"
                  type="primary"
                  size="sm"
                  isIcon
                  className="kuika-run__publish-split"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (this.props.sd.configurations.length === 0) return;
                    this.setVisible(!this.state.popupVisible);
                    setTimeout(() => {
                      this.props.sd.handleExecutablesSentMessageVisibility(false);
                    }, 500);
                  }}
                >
                  <KuikaSvg name="dots" width={18} height={18} />
                </KuikaButton>
              </PublishButton>
            )
          }
        >
          <div
            data-testid="headerPreviewButton"
            data-onboarding-id="header_toolbar-right_preview"
            className="kuika-flex-center"
            // Icon-only control: the `title` is the idle hover hint the forced status tooltip
            // doesn't cover; while running, the icon flips to ✕ and the tooltip says
            // "click to cancel preview".
            title={isLoading ? "Cancel preview" : "Preview"}
          >
            {isLoading ? (
              // Both glyphs are painted and CSS picks one on :hover / :focus-visible, rather than a
              // hover flag in state: this button is a PureSdComponent whose checkNeedRender returns
              // false, so re-rendering it on every pointer cross would be new work for a swap the
              // cascade does for free — and the keyboard path comes with it instead of needing its
              // own handler. Resting state is the spinner (the run IS in progress); the ✕ is the
              // affordance, revealed on approach.
              <span className="kuika-run__preview-progress">
                {/* Its own spinner, not KuikaButton's `loading` prop: that prop sets `disabled` and
                  drops `onClick`, and this button has to stay clickable while it runs — clicking
                  IS the cancel. A fading arc (toolbar-right.scss) reads as motion on the blue
                  fill, where the kit's even ring did not. */}
                <span className="kuika-run__preview-spinner" />
                <KuikaSvg name="x" className="kuika-run__preview-cancel" width={18} height={18} />
              </span>
            ) : (
              <KuikaSvg
                name={buttonIcon}
                width={18}
                height={18}
                data-onboarding-id="header_toolbar-right_preview-button-play-icon"
              />
            )}
            {/* The button grows to say how a run ended, and shrinks back to its icon. The clip
              animates its column from 0fr to 1fr, so the width follows the word without a measured
              size; the word is keyed by result, so each one fades in once. */}
            <span className={`kuika-run__status${statusLabel ? " kuika-run__status--shown" : ""}`} aria-hidden="true">
              <span className="kuika-run__status-clip">
                {statusLabel && (
                  <span key={previewStatus} className="kuika-run__status-text" data-testid="headerPreviewStatus">
                    {statusLabel}
                  </span>
                )}
              </span>
            </span>
            {/* The run's start and its result, announced once each. */}
            <span className="kuika-visually-hidden" role="status" aria-live="polite">
              {isLoading && loadingSource === "preview" ? "Preview running" : (statusLabel ?? "")}
            </span>
          </div>
        </KuikaButton>
        <WorkflowVersionDetailsModal />
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const mapDispatchToProps = (dispatch) => ({
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb))
});

const runButton = connect(null, mapDispatchToProps)(withContext<IRunButtonProps>(withSD<IRunButtonProps>(RunButton)));
export { runButton as RunButton };
